import {
  faBan,
  faFileImage,
  faFileVideo,
  faPause,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InputAdornment } from '@mui/material';
import StyledButton from 'components/general/StyledButton';
import StyledSlider from 'components/general/StyledSlider';
import LabeledInput from 'components/general/inputs/LabeledInput';
import { useCallback, useRef, useState } from 'react';
import theme from 'theme';
import useStyles from './styles';

const inactiveStyles = {
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.disabled,
};

const MediaMenu = ({ viewerRef, recording, setRecording, defaultFilename }) => {
  const { classes } = useStyles();
  const recorderRef = useRef(new MediaRecorder(new MediaStream()));
  const chunks = useRef([]);
  const [mode, setMode] = useState('image');
  const [loading, setLoading] = useState(false);
  const [filename, setFilename] = useState(defaultFilename || 'scenario_capture');
  const [resolutionScale, setResolutionScale] = useState(
    viewerRef.current.cesiumElement.resolutionScale
  );
  const [bitRate, setBitRate] = useState(5_000_000);
  const [recordingPaused, setRecordingPaused] = useState(false);

  const onDataAvailable = useCallback((e) => chunks.current.push(e.data), [chunks]);
  const onStop = useCallback(
    (e) => {
      const blob = new Blob(chunks.current, { type: 'video/webm' });
      const link = document.createElement('a');
      link.download = `${filename}.webm`;
      link.href = window.URL.createObjectURL(blob);
      link.click();
      link.remove();
      chunks.current = [];
      setRecording(false);
    },
    [chunks, filename, setRecording]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 220,
      }}
    >
      <div style={{ display: 'inline-flex', marginBottom: 8 }}>
        <Button
          style={{ borderRadius: '3px 0px 0px 3px', ...(mode !== 'image' ? inactiveStyles : {}) }}
          onClick={() => setMode('image')}
          disabled={recording}
          color="primary"
          variant="contained"
        >
          <div style={{ fontSize: 20, padding: '0px 0px 0px 4px' }}>
            <FontAwesomeIcon icon={faFileImage} />
          </div>
        </Button>
        <Button
          style={{
            borderRadius: '0px 3px 3px 0px',
            ...(mode !== 'video' ? inactiveStyles : {}),
          }}
          onClick={() => setMode('video')}
          disabled={loading}
          color="primary"
          variant="contained"
        >
          <div style={{ fontSize: 20, padding: '0px 4px 0px 0px' }}>
            <FontAwesomeIcon icon={faFileVideo} />
          </div>
        </Button>
      </div>

      <LabeledInput
        name="Filename"
        variant="outlined"
        defaultValue={filename}
        disabled={loading || recording}
        onChange={(event) => {
          setFilename(event.target.value);
        }}
        endAdornment={
          <InputAdornment position="end">{mode === 'image' ? '.png' : '.webm'}</InputAdornment>
        }
      />

      {mode === 'image' && (
        <>
          <h6>Resolution</h6>
          <StyledSlider
            className={classes.mediaSlider}
            defaultValue={resolutionScale}
            step={0.1}
            min={0.1}
            max={2}
            valueLabelDisplay="auto"
            onChange={(e, v) => setResolutionScale(v)}
          />
          <StyledButton
            type="button"
            onClick={() => {
              setLoading(true);
              const res = viewerRef.current.cesiumElement.resolutionScale;
              viewerRef.current.cesiumElement.resolutionScale = resolutionScale;
              viewerRef.current.cesiumElement.render();
              const link = document.createElement('a');
              link.download = `${filename}.png`;
              viewerRef.current.cesiumElement.canvas.toBlob((blob) => {
                link.href = window.URL.createObjectURL(blob);
                link.click();
                link.remove();
                viewerRef.current.cesiumElement.resolutionScale = res;
                setLoading(false);
              });
            }}
            disabled={loading}
            dontDisableInReadOnly
          >
            <h4>Capture</h4>
          </StyledButton>
        </>
      )}

      {mode === 'video' && (
        <>
          <h6>Bit Rate</h6>
          <StyledSlider
            className={classes.mediaSlider}
            defaultValue={bitRate}
            step={100_000}
            min={2_500_000}
            max={6_000_000}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v / 1_000_000} Mbps`}
            onChange={(e, v) => setBitRate(v)}
          />

          <StyledButton
            type="button"
            onClick={() => {
              if (recording) {
                recorderRef.current.stop();
              } else {
                recorderRef.current = new MediaRecorder(
                  viewerRef.current.cesiumElement.canvas.captureStream(30),
                  { videoBitsPerSecond: 10_000_000 }
                );
                chunks.current = [];
                recorderRef.current.addEventListener('dataavailable', onDataAvailable);
                recorderRef.current.addEventListener('stop', onStop);
                recorderRef.current.start();
                setRecording(true);
              }
            }}
            dontDisableInReadOnly
          >
            <h4> {recording ? 'Stop' : 'Record'} </h4>
          </StyledButton>
          {recording && (
            <div style={{ display: 'inline-flex' }}>
              <StyledButton
                type="button"
                style={{ borderRadius: '3px 0px 0px 3px' }}
                min
                onClick={() => {
                  if (recorderRef.current.state === 'paused') {
                    recorderRef.current.resume();
                    setRecordingPaused(false);
                  } else {
                    recorderRef.current.pause();
                    setRecordingPaused(true);
                  }
                }}
                dontDisableInReadOnly
              >
                <div>
                  <FontAwesomeIcon icon={recordingPaused ? faPlay : faPause} />
                </div>
              </StyledButton>
              <StyledButton
                type="button"
                style={{ borderRadius: '0px 3px 3px 0px' }}
                min
                onClick={() => {
                  recorderRef.current.removeEventListener('dataavailable', onDataAvailable);
                  recorderRef.current.removeEventListener('stop', onStop);
                  recorderRef.current.stop();
                  recorderRef.current = null;
                  chunks.current = [];
                  setRecording(false);
                }}
                dontDisableInReadOnly
              >
                <div>
                  <FontAwesomeIcon icon={faBan} />
                </div>
              </StyledButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MediaMenu;
